import { QueryClientProvider } from '@tanstack/react-query';
import { useEffect, type ReactNode } from 'react';
import { I18nextProvider, useTranslation } from 'react-i18next';
import { ToastProvider, useToast } from '@/ui';
import { describeError } from './api/errors';
import { AppServicesContext } from './AppServicesContext';
import type { AppServices } from './services';
import { ActiveRecordingProvider } from './session/ActiveRecordingContext';
import { ThemeProvider, type ThemePreference } from './theme/ThemeProvider';

/** Уведомление об ошибке мутации, если экран не обработал её сам (meta.silent). */
function GlobalErrorToasts({ services }: { services: AppServices }) {
  const toast = useToast();
  const { t, i18n } = useTranslation('errors');
  useEffect(
    () =>
      services.mutationErrors.subscribe((error) => {
        const info = describeError(error, t, i18n);
        if (info.kind === 'unauthorized' || info.kind === 'mfa') return;
        toast.show({ tone: 'error', title: info.title, description: info.description });
      }),
    [services, toast, t, i18n],
  );
  return null;
}

export function AppProviders({ services, children, theme }: { services: AppServices; children: ReactNode; theme?: ThemePreference }) {
  return (
    <I18nextProvider i18n={services.i18n}>
      <AppServicesContext.Provider value={services}>
        <QueryClientProvider client={services.queryClient}>
          <ThemeProvider initial={theme}>
            <ToastProvider>
              <ActiveRecordingProvider>
                <GlobalErrorToasts services={services} />
                {children}
              </ActiveRecordingProvider>
            </ToastProvider>
          </ThemeProvider>
        </QueryClientProvider>
      </AppServicesContext.Provider>
    </I18nextProvider>
  );
}
